import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SbomService } from '../../../Services/sbom.service';
import {
  SbomProject, SbomComponent, SbomDocument, SbomAuditLog,
  SbomProjectStatusLabels, COMPONENT_TYPES, DOCUMENT_TYPES, HASH_ALGORITHMS,
  SecurityAssessment, SecurityRequirement, SL_DESCRIPTIONS
} from '../../../Models/sbom.model';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SbomProjectFormComponent } from '../sbom-project-form/sbom-project-form.component';
import { SbomComponentFormComponent } from '../sbom-component-form/sbom-component-form.component';

@Component({
  selector: 'app-sbom-detail',
  templateUrl: './sbom-detail.component.html',
  styleUrls: ['./sbom-detail.component.css']
})
export class SbomDetailComponent implements OnInit {
  project: SbomProject | null = null;
  loading = true;
  statusLabels = SbomProjectStatusLabels;
  componentTypes = COMPONENT_TYPES;
  documentTypes = DOCUMENT_TYPES;
  slDescriptions = SL_DESCRIPTIONS;

  // Document upload
  uploadDescription = '';
  uploadDocType = '';
  uploading = false;

  // Component display columns
  displayedColumns = ['name', 'version', 'componentType', 'supplier', 'isCritical', 'actions'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sbomService: SbomService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) this.loadProject(id);
  }

  loadProject(id: number): void {
    this.loading = true;
    this.sbomService.getProject(id).subscribe({
      next: (project) => {
        this.project = project;
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Projekt nicht gefunden', 'OK', { duration: 4000 });
        this.router.navigate(['/sbom']);
        this.loading = false;
      }
    });
  }

  editProject(): void {
    if (!this.project) return;
    const dialogRef = this.dialog.open(SbomProjectFormComponent, {
      width: '640px',
      data: { ...this.project },
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadProject(this.project!.id);
        this.snackBar.open('Projekt aktualisiert', 'OK', { duration: 3000 });
      }
    });
  }

  // ───── Components ─────

  addComponent(): void {
    if (!this.project) return;
    const dialogRef = this.dialog.open(SbomComponentFormComponent, {
      width: '600px',
      data: { projectId: this.project.id },
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadProject(this.project!.id);
        this.snackBar.open('Komponente hinzugefügt', 'OK', { duration: 3000 });
      }
    });
  }

  editComponent(component: SbomComponent): void {
    if (!this.project) return;
    const dialogRef = this.dialog.open(SbomComponentFormComponent, {
      width: '600px',
      data: { projectId: this.project.id, component: { ...component } },
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadProject(this.project!.id);
        this.snackBar.open('Komponente aktualisiert', 'OK', { duration: 3000 });
      }
    });
  }

  deleteComponent(component: SbomComponent): void {
    if (!this.project) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Komponente löschen', message: `"${component.name}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.sbomService.deleteComponent(this.project!.id, component.id).subscribe({
          next: () => {
            this.loadProject(this.project!.id);
            this.snackBar.open('Komponente gelöscht', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // ───── Documents ─────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.project) return;

    this.uploading = true;
    const file = input.files[0];
    this.sbomService.uploadDocument(this.project.id, file, this.uploadDescription, this.uploadDocType).subscribe({
      next: () => {
        this.uploading = false;
        this.uploadDescription = '';
        this.uploadDocType = '';
        input.value = '';
        this.loadProject(this.project!.id);
        this.snackBar.open('Dokument hochgeladen', 'OK', { duration: 3000 });
      },
      error: () => {
        this.uploading = false;
        this.snackBar.open('Fehler beim Hochladen', 'OK', { duration: 4000 });
      }
    });
  }

  downloadDocument(doc: SbomDocument): void {
    if (!this.project) return;
    this.sbomService.downloadDocument(this.project.id, doc.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.originalFileName;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.snackBar.open('Fehler beim Download', 'OK', { duration: 4000 })
    });
  }

  deleteDocument(doc: SbomDocument): void {
    if (!this.project) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Dokument löschen', message: `"${doc.originalFileName}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.sbomService.deleteDocument(this.project!.id, doc.id).subscribe({
          next: () => {
            this.loadProject(this.project!.id);
            this.snackBar.open('Dokument gelöscht', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // ───── Export ─────

  exportAsJson(): void {
    if (!this.project) return;
    this.sbomService.exportSbom(this.project.id).subscribe({
      next: (data) => {
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SBOM_${this.project!.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  previewDocument(doc: SbomDocument): void {
    if (!this.project) return;
    const url = this.sbomService.getDocumentPreviewUrl(this.project.id, doc.id);
    window.open(url, '_blank');
  }

  isPreviewable(doc: SbomDocument): boolean {
    const previewTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp', 'text/plain', 'text/html'];
    return previewTypes.includes(doc.contentType);
  }

  getLogoUrl(logoPath: string): string {
    return this.sbomService.getCustomerLogoUrl(logoPath);
  }

  // ───── Security Assessment ─────

  startSecurityCheck(): void {
    if (!this.project) return;
    this.router.navigate(['/sbom/projekt', this.project.id, 'sicherheitscheck']);
  }

  toggleRequirement(req: SecurityRequirement): void {
    this.sbomService.toggleSecurityRequirement(req.id).subscribe({
      next: (updated) => {
        req.isCompleted = updated.isCompleted;
        req.completedAt = updated.completedAt;
        req.completedBy = updated.completedBy;
        if (this.project?.securityAssessment) {
          this.project.securityAssessment.completedRequirements =
            this.project.securityAssessment.requirements.filter(r => r.isCompleted).length;
        }
      },
      error: () => this.snackBar.open('Fehler beim Aktualisieren', 'OK', { duration: 4000 })
    });
  }

  getSecurityProgress(): number {
    const a = this.project?.securityAssessment;
    if (!a || a.totalRequirements === 0) return 0;
    return Math.round((a.completedRequirements / a.totalRequirements) * 100);
  }

  getRequirementsByCategory(category: string): SecurityRequirement[] {
    return this.project?.securityAssessment?.requirements?.filter(r => r.category === category) ?? [];
  }

  goBack(): void {
    this.router.navigate(['/sbom']);
  }
}
